import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RESTServiceV2 } from 'src/app/shared/services/rest.service.v2';

export interface OtdInvoiceRow {
  id: string;
  codes: string[];
  idOrigin?: string;
  originalCode?: string;
  status?: string;
  debtorName: string;
  debtorRuc: string;
  providerName: string;
  currency: string;
  netAmount: string;
  advanceAmount: string;
  paymentDate: string;
  remainingDays: number;
  investmentsCount: number;
}

export interface OtdSaleOperation {
  id: string;
  codes: string[];
  partitionsCount: number;
  advancePaymentDate: string;
  latestInvestmentDate?: string;
  remainingDays: number;
  netAmount: string;
  fundCommission: string;
  transferAmount: string;
  finsmartReturn?: string;
}

export interface OtdSaleDetail {
  operations: OtdSaleOperation[];
  totals: {
    netAmount: string;
    fundCommission: string;
    transferAmount: string;
    investmentsToClose: number;
    finsmartReturn?: string;
  };
}

export interface OtdSale {
  invoiceIds: string[];
  saleDate: string;
  finalizeDate: string;
  fundTea: number;
  transactionIds: string[];
  voucher?: string;
}

@Injectable({ providedIn: 'root' })
export class OtdService {
  constructor(private rest: RESTServiceV2) {}

  getInvoices(): Observable<any> {
    return this.rest.get('/otd/invoices');
  }

  getSaleDetail(invoiceIds: string[], saleDate: string, fundTea: string, finalizeDate: string): Observable<any> {
    return this.rest.get('/otd/sale-detail', {
      params: { invoiceIds: invoiceIds.join(','), saleDate, fundTea, finalizeDate },
    });
  }

  confirmSale(sale: OtdSale): Observable<any> {
    return this.rest.postFormData('/otd/sales', saleFormData(sale));
  }
}

const VOUCHER_EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

function saleFormData(sale: OtdSale): FormData {
  const formData = new FormData();
  sale.invoiceIds.forEach((invoiceId) => formData.append('invoiceIds', invoiceId));
  formData.append('saleDate', sale.saleDate);
  formData.append('finalizeDate', sale.finalizeDate);
  formData.append('fundTea', String(sale.fundTea));
  sale.transactionIds.forEach((transactionId) => formData.append('transactionIds', transactionId));
  if (sale.voucher) {
    const voucher = voucherBlob(sale.voucher);
    formData.append('voucher', voucher, `voucher.${VOUCHER_EXTENSION_BY_CONTENT_TYPE[voucher.type]}`);
  }
  return formData;
}

function voucherBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const contentType = header.substring('data:'.length, header.indexOf(';'));
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new Blob([bytes], { type: contentType });
}
